import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { useI18n, useSelector } from '~Hooks';
import { FormattedModificationGroup } from '~Hooks/ItemModifications/useItemModifications';
import Group, { ExpandedGroup, GROUP_TYPE } from './Components/Group';
import Info from './Components/Info';
import BottomButtons from './Components/BottomButtons';
import { getItemPlaceholderImage } from '../../../../themes/getTheme';
import { Box } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { LocalItem } from '~Types';
import _ from 'lodash';

const Wrapper = styled.div`
  overflow-y: auto;
  height: 100%;
`;

export const FULL_IMAGE_SIZE = {
  width: 1080,
  height: 720,
};

const Image = styled.div<{ url: any }>`
  width: ${FULL_IMAGE_SIZE.width}px;
  height: ${FULL_IMAGE_SIZE.height}px;
  border-radius: 80px 80px 0px 0px;
  background-image: url(${(props) => props.url});
  background-position: center;
  background-size: cover;

  @media (orientation: landscape) {
    height: 615px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 0 32px 0;

  @media (orientation: landscape) {
    padding: 0 0 10px 0;
  }
`;

const Content = styled.div`
  padding: 56px 164px 464px 164px;

  @media (orientation: landscape) {
    padding: 40px 60px 230px 60px;
  }
`;

const ItemName = styled.div`
  color: rgb(19, 19, 22);
  font-family: Eina01-SemiBold;
  font-size: 64px;
  font-weight: 600;
  line-height: 72px;

  @media (orientation: landscape) {
    font-size: 48px;
    line-height: 54px;
  }
`;

const Gradient = styled.div<{ hidden?: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  background: linear-gradient(180deg, rgba(245, 245, 248, 0) 0%, rgb(255, 255, 255) 100%);
  height: 372px;
  width: 100%;
  pointer-events: none;
  opacity: ${({ hidden }) => (hidden ? 0 : 1)};
  transition: opacity 0.1s;

  @media (orientation: landscape) {
    height: 100px;
    background: linear-gradient(180deg, rgba(245, 245, 248, 0) 0%, rgb(255, 255, 255, 0.5) 100%);
  }
`;

const Description = styled.div`
  color: rgb(19, 19, 22);
  font-size: 32px;
  line-height: 45px;

  @media (orientation: landscape) {
    font-size: 24px;
    line-height: 33px;
  }
`;

interface GroupWithId extends ExpandedGroup {
  id: string;
  required: boolean;
}

interface Props {
  item: LocalItem;
  price: number;
  isEdit?: boolean;
  cartItemsQuantity: number;
  modificationGroups: FormattedModificationGroup[];
  quantity: number;
  hideBottomButtons?: boolean;
  onSelectVariation: (id: string) => void;
  onChangeQuantity?: (value: number) => void;
  onSubmit: () => void;
}

const Details: React.FunctionComponent<Props> = ({
  item,
  price,
  isEdit,
  cartItemsQuantity,
  modificationGroups,
  onSelectVariation,
  quantity,
  onChangeQuantity,
  onSubmit,
  hideBottomButtons,
}: Props) => {
  const { i18n } = useI18n();
  const companyId = useSelector((state) => state.company);

  const valid = useMemo(() => modificationGroups.every((group) => group.valid), [
    modificationGroups,
  ]);
  const formattedModificationGroups: GroupWithId[] = modificationGroups.map((g) => ({
    id: g.id,
    title: g.title,
    type: g.type === 'MULTIPLE' ? GROUP_TYPE.MULTIPLE : GROUP_TYPE.SINGLE,
    description: i18n('Item.ChooseOption'),
    expandedByDefault: g.expandedByDefault,
    valid: g.valid,
    collapsible: true,
    onSelect: (id: string) => {
      onSelectVariation(id);
    },
    items: g.variations.map((gv) => ({
      id: gv.id,
      name: gv.name,
      price: gv.price,
      imageUrl: gv?.imageUrl,
      selected: gv.selected,
      isOutOfStock: gv.isOutOfStock,
    })),
    required: g.required,
    moreText: i18n('General.More'),
    preselectedVariations: g.preselectedVariations,
  }));

  const useImagePlaceholder = !item.imageUrl;
  const imageUrl = useImagePlaceholder
    ? getItemPlaceholderImage(companyId)
    : `${item.imageUrl}/${FULL_IMAGE_SIZE.width * 2}x${FULL_IMAGE_SIZE.height * 2}`;

  const hasVariations = Boolean(formattedModificationGroups.length);

  return (
    <Wrapper>
      <Image url={imageUrl} />
      <Content>
        <Header>
          <ItemName>{item.title}</ItemName>
          {hasVariations && item.description && <Info description={item.description} />}
        </Header>

        <Box display="flex" flexDirection="column" gap="small">
          {!hasVariations && item.description && <Description>{item.description}</Description>}
          {item.allergyTags && item.allergyTags.length > 0 && (
            <Paragraph level={4} color="var(--colorBlueGray1200)">
              {i18n('SSK.Allergens')}:{' '}
              {item.allergyTags?.map((tag) => _.capitalize(tag)).join(', ')}
            </Paragraph>
          )}
        </Box>

        {formattedModificationGroups.map((g) => (
          <Group
            type={g.type}
            title={g.title}
            key={g.id}
            items={g.items}
            expandedByDefault={
              g.expandedByDefault || (g.required && g.preselectedVariations.length === 0)
            }
            required={g.required}
            onSelect={g.onSelect}
            preselectedVariations={g.preselectedVariations}
          />
        ))}

        <BottomButtons
          isEdit={isEdit}
          cartItemsQuantity={cartItemsQuantity}
          hidden={hideBottomButtons}
          quantity={quantity}
          onChangeQuantity={onChangeQuantity}
          amount={price}
          onSubmit={onSubmit}
          valid={valid}
        />
        <Gradient hidden={hideBottomButtons} />
      </Content>
    </Wrapper>
  );
};

export default Details;
